import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { FileText, Download, Filter, BookOpen } from "lucide-react";
import { useLanguage } from "../../i18n/LanguageContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function DocumentListPage({ title, subtitle, titleKey, subtitleKey, endpoint, collection, facetField = "category", accentColor = "#00E5FF", Icon = BookOpen }) {
  const { t } = useLanguage();
  const resolvedTitle = titleKey ? t(titleKey) : title;
  const resolvedSubtitle = subtitleKey ? t(subtitleKey) : subtitle;
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    axios.get(`${API}/${endpoint}`, { withCredentials: true })
      .then(r => setItems(r.data || [])).catch(() => setItems([]));
  }, [endpoint]);

  const facets = useMemo(() => ["all", ...new Set(items.map(i => i[facetField]))], [items, facetField]);
  const filtered = filter === "all" ? items : items.filter(i => i[facetField] === filter);

  const handleDownload = async (doc) => {
    setDownloading(doc.id);
    try {
      const res = await axios.get(`${API}/library/document/${collection}/${doc.id}`,
        { withCredentials: true, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(doc.title || "document").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } finally { setDownloading(null); }
  };

  return (
    <div className="px-6 py-6 space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center border"
            style={{ borderColor: `${accentColor}55`, backgroundColor: `${accentColor}15` }}>
            <Icon className="w-6 h-6" style={{ color: accentColor }} />
          </div>
          <div>
            <h1 className="text-white tracking-tight" style={{ fontFamily: "Chivo, sans-serif" }}>{resolvedTitle}</h1>
            <p className="text-xs text-[#A0AEC0] mt-1 tracking-widest uppercase">{resolvedSubtitle} · {items.length} {t("lib.docs_count") || "docs"}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Filter className="w-3.5 h-3.5 text-[#A0AEC0]" />
          {facets.map(f => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f}`}
              className={`px-3 py-1.5 text-xs tracking-wider transition-all ${
                filter === f
                  ? "bg-[#00E5FF] text-black font-bold"
                  : "bg-[#1F242D] text-[#CBD5E1] hover:text-white hover:bg-[#30363D]"
              }`}>
              {f === "all" ? t("common.all") : f}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {filtered.map(doc => (
          <div key={doc.id} data-testid="doc-card"
            className="group bg-[#0D0F12] border border-white/[0.04] hover:border-[#00E5FF]/40 transition-all p-5 flex flex-col">
            <div className="flex items-start justify-between mb-4">
              <div className="w-11 h-11 flex items-center justify-center border"
                style={{ borderColor: `${accentColor}40`, backgroundColor: `${accentColor}12` }}>
                <FileText className="w-5 h-5" style={{ color: accentColor }} />
              </div>
              <span className="text-[10px] text-[#A0AEC0] tracking-widest uppercase">PDF · {doc.pages || 12} {t("lib.pages")}</span>
            </div>
            <p className="text-[10px] tracking-widest uppercase mb-2" style={{ color: accentColor }}>
              {doc[facetField]}
            </p>
            <h3 className="text-sm font-bold text-white mb-2 leading-snug" style={{ fontFamily: "Chivo, sans-serif" }}>
              {doc.title}
            </h3>
            <p className="text-xs text-[#CBD5E1] leading-relaxed mb-5 line-clamp-4 flex-1">
              {doc.summary}
            </p>
            <button onClick={() => handleDownload(doc)} disabled={downloading === doc.id}
              data-testid={`download-${doc.id}`}
              className="w-full flex items-center justify-center gap-2 py-2.5 bg-[#1F242D] border border-white/[0.06] text-white text-xs font-semibold tracking-wider uppercase hover:bg-[#00E5FF] hover:text-black hover:border-[#00E5FF] transition-all disabled:opacity-50">
              <Download className="w-3.5 h-3.5" /> {downloading === doc.id ? "..." : t("action.download")}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
